import { Injectable } from '@angular/core';

import { BehaviorSubject, finalize, map, Observable, of, tap } from 'rxjs';

import { TextCode } from '../../models/text-code.interface';
import {
  GetAllSubTenantsGQL,
  SubTenantsListWithApprovedWireRecipientGQL,
  Originator,
} from 'src/app/graphql/generated';
import { UtilsService } from '../utils/utils.service';

@Injectable({
  providedIn: 'root',
})
export class SubTenantsService {
  private readonly originatorsList: BehaviorSubject<TextCode[]> =
    new BehaviorSubject<TextCode[]>([]);
  private readonly isInitialSubtenantsFetchComplete: BehaviorSubject<boolean> =
    new BehaviorSubject(false);
  public readonly isInitialSubtenantsFetchComplete$: Observable<boolean> =
    this.isInitialSubtenantsFetchComplete.asObservable();

  constructor(
    private readonly getAllSubTenantsGql: GetAllSubTenantsGQL,
    private readonly originatorsListWithApprovedWireRecipientGql: SubTenantsListWithApprovedWireRecipientGQL,
    private readonly utilsService: UtilsService
  ) {}

  getSubTenants(isRefresh: boolean = false): Observable<TextCode[]> {
    // Return cached data if available and not forcing refresh
    if (!isRefresh && this.originatorsList.value.length > 0) {
      return of(this.originatorsList.value);
    }

    // Fetch from API and update cache
    return this.getAllSubTenantsGql.fetch().pipe(
      map(({ data }) => {
        const originators =
          this.utilsService.getOriginatorListItems<Originator>(
            data?.originators
          );
        return originators.flatMap((subTenant) =>
          subTenant.originatorId
            ? [
                {
                  text: subTenant.originatorName ?? '',
                  code: subTenant.originatorId,
                } satisfies TextCode,
              ]
            : []
        );
      }),
      map((originators) =>
        originators.sort((a, b) =>
          a.text.toLowerCase().localeCompare(b.text.toLowerCase())
        )
      ),
      tap((originators) => {
        this.originatorsList.next(originators);
      }),
      finalize(() => this.setInitialSubTenantsFetchComplete(true))
    );
  }

  getSubTenantsListWithApprovedWireRecipient(
    wireRecipientId: string
  ): Observable<Originator[]> {
    return this.originatorsListWithApprovedWireRecipientGql
      .fetch({
        variables: {
          wireRecipientId: wireRecipientId,
        },
      })
      .pipe(
        map(({ data }) =>
          this.utilsService.getOriginatorListItems<Originator>(
            data?.originators
          )
        )
      );
  }

  public setInitialSubTenantsFetchComplete(isComplete: boolean): void {
    this.isInitialSubtenantsFetchComplete.next(isComplete);
  }
}
